import { CSSProperties, ReactElement, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Chip, SxProps, Theme, Typography, Tooltip, ClickAwayListener } from '@mui/material';

const ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;

const getChipIcon = ({
  total_roi,
  average_roi,
}: {
  total_roi: number;
  average_roi: number;
}): ReactElement => {
  if (total_roi > average_roi) {
    return (
      <AppIcon iconName="ThumbsUp" diameter="12px" color="Colors/Foreground/fg-success-secondary" />
    );
  }

  if (total_roi < average_roi) {
    return (
      <AppIcon iconName="ThumbsDown" diameter="12px" color="Colors/Foreground/fg-error-secondary" />
    );
  }

  return <AppIcon iconName="Equal" diameter="12px" color="Colors/Foreground/fg-quinary" />;
};

type ROILineChartHeaderProps = Readonly<{
  total_roi: number;
  average_roi: number;
}>;

export const ROILineChartHeader = ({ total_roi, average_roi }: ROILineChartHeaderProps) => {
  const [isMuiTooltipOpen, setIsMuiTooltipOpen] = useState(false);

  return (
    <Box sx={titleContentSx}>
      <Box sx={titleSx}>
        <Typography variant="text-lg-semibold" component="div">
          {ROI_LINE_CHART_STRINGS.title}
        </Typography>
        <ClickAwayListener onClickAway={() => setIsMuiTooltipOpen(false)}>
          <Tooltip
            open={isMuiTooltipOpen}
            onClose={() => setIsMuiTooltipOpen(false)}
            placement="top"
            arrow
            disableFocusListener
            disableHoverListener
            disableTouchListener
            sx={tooltipSx}
            componentsProps={{
              tooltip: {
                sx: toolTipComponentSx,
              },
            }}
            title={ROI_LINE_CHART_STRINGS.tooltip}
          >
            <Box sx={tooltipIconSx} onClick={() => setIsMuiTooltipOpen((prev) => !prev)}>
              <AppIcon iconName="InfoCircle" diameter="20px" color="Colors/Foreground/fg-quinary" />
            </Box>
          </Tooltip>
        </ClickAwayListener>
      </Box>
      <Box sx={totalInvestmentSx}>
        <Typography variant="display-sm-semibold" component="div">
          {total_roi}%
        </Typography>
        <Chip
          variant="badge"
          size="md"
          type="leading"
          label={ROI_LINE_CHART_STRINGS.chipLabel(total_roi, average_roi)}
          icon={getChipIcon({ total_roi, average_roi })}
        />
      </Box>
    </Box>
  );
};

const titleContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const totalInvestmentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  mb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  flexDirection: { xs: 'row', sm: 'row' },
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-sm'],
});

const tooltipIconSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const tooltipSx: SxProps<Theme> = {
  fontFamily: 'Sarabun, sans-serif',
  whiteSpace: 'pre-line',
  '& .MuiTooltip-tooltip': {
    fontFamily: 'Sarabun, sans-serif',
    whiteSpace: 'pre-line',
  },
};

const toolTipComponentSx: CSSProperties = {
  fontFamily: 'Sarabun, sans-serif',
  whiteSpace: 'pre-line',
};
